import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    if (url) {
      setSelectedCategory(url);
      window.open(url, '_blank');
      if (selectRef.current) {
        selectRef.current.value = '';
      }
      setSelectedCategory('');
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background-50 via-background-100 to-accent-100/30"></div>

      {/* Hero image background */}
      <div className="absolute bottom-0 left-0 right-0 h-[55%] md:h-[60%] opacity-40">
        <img
          src="https://readdy.ai/api/search-image?query=Curated%20minimalist%20product%20flatlay%20featuring%20diverse%20ecommerce%20items%20including%20clothing%20accessories%20leather%20goods%20and%20lifestyle%20products%20arranged%20harmoniously%20on%20warm%20sand%20colored%20background%20soft%20natural%20window%20light%20from%20upper%20left%20editorial%20product%20photography%20with%20subtle%20shadows%20clean%20aesthetic%20no%20people&width=1600&height=900&seq=hero-v2-main-2026&orientation=landscape"
          alt="YOYBUY Spreadsheet products"
          className="w-full h-full object-cover object-top rounded-t-[60px] md:rounded-t-[100px]"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[55%] md:h-[60%] bg-gradient-to-t from-background-50/80 via-background-50/40 to-transparent rounded-t-[60px] md:rounded-t-[100px]"></div>

      {/* Navbar */}
      <nav className="relative z-20 w-full px-4 md:px-10 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <span className="text-lg md:text-xl font-heading font-bold text-foreground-900 tracking-tight">YOYBUY <span className="text-primary-500">SPREADSHEET</span></span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">Главная</Link>
          <Link to="/article/yoybuy-spreadsheet-polnoe-rukovodstvo" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Руководство по покупкам</Link>
          <a href="#categories" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Категории</a>
          <a href="#comparison" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Сравнение</a>
          <a href="#faq" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">FAQ</a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            YOYBUY Sheet
          </a>
        </div>

        {/* Mobile hamburger button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/80 backdrop-blur-sm border border-background-200 cursor-pointer"
          aria-label="Меню"
        >
          <i className={`text-xl text-foreground-800 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-72 h-full bg-background-50 shadow-lg flex flex-col py-6 px-5 gap-2 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-heading font-bold text-foreground-900">YOYBUY <span className="text-primary-500">SP</span></span>
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-600 text-xl"></i>
              </button>
            </div>
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 bg-primary-500/10 hover:bg-primary-500/20 transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-home-4-line text-primary-500"></i></span>
              Главная
            </Link>
            <Link
              to="/article/yoybuy-spreadsheet-polnoe-rukovodstvo"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-book-open-line text-foreground-500"></i></span>
              Руководство по покупкам
            </Link>
            <a
              href="#categories"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-price-tag-3-line text-foreground-500"></i></span>
              Категории
            </a>
            <a
              href="#comparison"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-scales-3-line text-foreground-500"></i></span>
              Сравнение
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-question-line text-foreground-500"></i></span>
              FAQ
            </a>

            <div className="border-t border-background-200 my-3 pt-3">
              <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Категории товаров</span>
            </div>
            <div className="flex flex-col gap-1 max-h-[40vh] overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <img src={cat.image} alt={cat.name} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="mt-auto pt-4 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                YOYBUY Sheet
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hero content */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center px-6 md:px-10 lg:px-16 pt-8 md:pt-16 pb-16 gap-10 lg:gap-16">
        {/* Left: Main content */}
        <div className="w-full lg:w-[55%] flex flex-col items-start gap-6 md:gap-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-background-50/80 backdrop-blur-sm rounded-full border border-background-200">
            <span className="w-2 h-2 rounded-full bg-primary-500"></span>
            <span className="text-xs font-medium text-foreground-600 tracking-wide uppercase">YOYBUY Spreadsheet 2026</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-extrabold text-foreground-950 leading-[1.1] text-balance">
            YOYBUY Spreadsheet —<br />
            самая полная таблица<br />
            для закупок из Китая
          </h1>

          <p className="text-base md:text-lg text-foreground-600 max-w-xl leading-relaxed">
            Управляйте закупками из Китая проще, быстрее и дешевле с YOYBUY Spreadsheet,
            Kakobuy Spreadsheet и Superbuy Spreadsheet.
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-fill text-primary-500"></i> 1000+ товаров</span>
            <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-fill text-primary-500"></i> 3 платформы</span>
            <span className="flex items-center gap-1.5"><i className="ri-checkbox-circle-fill text-primary-500"></i> Еженедельные обновления</span>
          </div>
        </div>

        {/* Right: Dropdown + Buttons */}
        <div className="w-full lg:w-[45%] flex flex-col items-center lg:items-end gap-5">
          <div className="w-full max-w-sm bg-background-50/85 backdrop-blur-md rounded-2xl p-6 border border-background-200/70 flex flex-col gap-5">
            {/* Category dropdown */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Выберите категорию</label>
              <div className="relative">
                <select
                  ref={selectRef}
                  onChange={handleCategoryChange}
                  defaultValue=""
                  className="w-full appearance-none bg-background-100 border border-background-300 rounded-xl px-4 py-3 pr-10 text-sm font-medium text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all cursor-pointer"
                >
                  <option value="" disabled>— Выберите категорию товаров —</option>
                  {categories.map((cat) => (
                    <option key={cat.name} value={cat.url}>{cat.name}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-foreground-400"></i>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all text-center whitespace-nowrap cursor-pointer"
              >
                YOYBUY Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-6 py-3 border-2 border-foreground-900 text-foreground-900 rounded-full text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-all text-center whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <p className="text-xs text-foreground-400 text-center">
              Перейдите на основной сайт для полного доступа
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}